import React, { useState, useEffect } from "react";
import { useFont } from "../../contexts/FontContext/FontContext";
import { Helmet } from "react-helmet-async";
import BackButton from "../BackButton/BackButton";
import LabelGeneratorProgressBar from "./LabelGeneratorProgressBar";
import LabelGeneratorConfirmationBox from "./LabelGeneratorConfirmationBox";
import LogoWrapper from "../LogoWrapper/LogoWrapper";
import TextInput from "../TextInput/TextInput";
import styles from "./LabelGenerator.module.css";

const progressBarStates = ["recipient", "sender", "dimensions", "confirmation"];
const progressBarStateTitles = ["To address.", "From address.", "Package dimensions.", "Confirm label details."];

const LabelGenerator = () => {
	const apiUrl = process.env.REACT_APP_API_URL;
	const token = localStorage.getItem("authToken");
	//const [boxName, setBoxName] = useState("");
	const [savedAddresses, setSavedAddresses] = useState([]);
	const [savedPackageBoxes, setSavedPackageBoxes] = useState([]);
	const [labelUrl, setLabelUrl] = useState(null);
	const [packageLength, setPackageLength] = useState(null);
    const [packageWidth, setPackageWidth] = useState(null);
    const [packageHeight, setPackageHeight] = useState(null);
    const [packageWeightLbs, setPackageWeightLbs] = useState(null);
    const [packageWeightOz, setPackageWeightOz] = useState(null);
   	const [progressBarStateIndex, setProgressBarStateIndex] = useState(0);
    const [progressBarStateTitle, setProgressBarStateTitle] = useState("To address");
    const [showPurchaseLabelLoader, setShowPurchaseLabelLoader] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [recipient, setRecipient] = useState("");
    const [recipientStreetOne, setRecipientStreetOne] = useState("");
    const [recipientStreetTwo, setRecipientStreetTwo] = useState("");
    const [recipientCity, setRecipientCity] = useState("");
    const [recipientState, setRecipientState] = useState("");
    const [recipientZip, setRecipientZip] = useState("");
    const [recipientCountry, setRecipientCountry] = useState("");
    const [referenceNumber, setReferenceNumber] = useState(null);
    const [saveBox, setSaveBox] = useState(false);
    const [saveRecipientAddress, setSaveRecipientAddress] = useState(false);
    const [selectedSavedAddress, setSelectedSavedAddress] = useState("");
    const [selectedCarrier, setSelectedCarrier] = useState("cheapest");
    const [sender, setSender] = useState("");
    const [senderStreetOne, setSenderStreetOne] = useState("");
    const [senderStreetTwo, setSenderStreetTwo] = useState("");
    const [senderCity, setSenderCity] = useState("");
    const [senderState, setSenderState] = useState("");
    const [senderZip, setSenderZip] = useState("");
    const [senderCountry, setSenderCountry] = useState("");
    const [showDropdown, setShowDropdown] = useState(false);
    const [shipmentData, setShipmentData] = useState(null);
    const [viewSavedPackageBoxes, setViewSavedPackageBoxes] = useState(false);
    const fontFamilyOne = useFont().scheme_one;

    useEffect(() => { 
    	setProgressBarStateTitle(progressBarStateTitles[progressBarStateIndex]); 
    	setViewSavedPackageBoxes(false);
    }, [progressBarStateIndex]); //change page title based on progress bar index

    //fetch saved address to render in dropdown
	useEffect(() => {
		const fetchSavedAddresses = async () => {
			try {
				const response = await fetch(`${apiUrl}/api/get_saved_addresses/`, {
					method: "GET",
					headers: { "Authorization": `Bearer ${token}` }
				});
				const data = await response.json();
				setSavedAddresses(data.addresses);
			} catch (error) { console.error("Error fetching saved addresses:", error); }
		};
		fetchSavedAddresses();
	}, [apiUrl, token]);

	//fetch pre saved package box dimensions
	useEffect(() => {
		const fetchSavedPackageBoxes = async () => {
			try {
				const response = await fetch(`${apiUrl}/api/get_saved_package_boxes/`, {
					method: "GET",
					headers: { "Authorization": `Bearer ${token}` }
				});
				const data = await response.json();
				setSavedPackageBoxes(data);
			} catch (error) { console.error("Error fetching saved package boxes:", error); }
		};
		fetchSavedPackageBoxes();
	}, [apiUrl, token])

    //render/take away dropdown
    const handleSearchAddressesChange = (e) => { 
    	const value = e.target.value;
    	setSelectedSavedAddress(value); 
    	if (value !== '') { setShowDropdown(true); }
    	else { setShowDropdown(false); }
    };

    //back button
    const handleBack = () => {
    	if (progressBarStateIndex > 0) { setProgressBarStateIndex(progressBarStateIndex - 1); }
    }

    //forward button
    const handleNext = () => {
    	if (progressBarStateIndex + 1 === 3) { 
    		const data = {
				from_address: {name: sender, street1: senderStreetOne, street2: senderStreetTwo, city: senderCity, state: senderState, zip: senderZip, country: senderCountry},
			  	to_address: {name: recipient, street1: recipientStreetOne, street2: recipientStreetTwo, city: recipientCity, state: recipientState, zip: recipientZip, country: recipientCountry},
			  	parcel: {length: parseFloat(packageLength), width: parseFloat(packageWidth), height: parseFloat(packageHeight), weight:(parseFloat(packageWeightLbs) || 0) * 16 + (parseFloat(packageWeightOz) || 0)},			  	
			};
			setShipmentData(data);
    	}
    	if (progressBarStateIndex < progressBarStates.length) { setProgressBarStateIndex(progressBarStateIndex + 1); }
    }

    //set address and populate fields.
    const handleAddressClick = (addr) => {
    	setSelectedSavedAddress(addr);
    	setShowDropdown(false);
    	const addressComponents = addr.split(',').map(component => component.trim());
    	const addressLength = addressComponents.length;
    	const recipientIndex = 0;
    	let streetOneIndex = 1, streetTwoIndex = null, cityIndex, stateIndex, zipIndex, countryIndex;
		if (addressLength === 6) { [cityIndex, stateIndex, zipIndex, countryIndex] = [2, 3, 4, 5]; }
		else if (addressLength === 7) { [streetTwoIndex, cityIndex, stateIndex, zipIndex, countryIndex] = [2, 3, 4, 5, 6]; }
    	else { alert("problem with address formatting for selected address") }
    	const recipient = addressComponents[recipientIndex], streetOne = addressComponents[streetOneIndex], streetTwo = streetTwoIndex ? addressComponents[streetTwoIndex] : null, city = addressComponents[cityIndex], state = addressComponents[stateIndex], zip = addressComponents[zipIndex], country = addressComponents[countryIndex];
    	setRecipient(recipient);
    	setRecipientStreetOne(streetOne);
    	if (streetTwo) { setRecipientStreetTwo(streetTwo); }
    	setRecipientCity(city);
    	setRecipientState(state);
    	setRecipientZip(zip);
    	setRecipientCountry(country);
    }

    const dimensionsAlreadySaved = () => {
		return savedPackageBoxes.some(box => 
	    	box.length === parseFloat(packageLength) &&
	    	box.width === parseFloat(packageWidth) &&
	    	box.height === parseFloat(packageHeight) &&
	    	box.weight === (parseFloat(packageWeightLbs) || 0) * 16 + (parseFloat(packageWeightOz) || 0)
	  	);
	};

    //validator that makes sure all required fields are not null
    const requiredFieldsFilled = () => {
		if (progressBarStateIndex === 0) { return recipient && recipientStreetOne && recipientCity && recipientState && recipientZip && recipientCountry; }
	  	else if (progressBarStateIndex === 1) { return sender && senderStreetOne && senderCity && senderState && senderZip && senderCountry; }
	  	else if (progressBarStateIndex === 2) { return packageLength && packageWidth && packageHeight && packageWeightOz; }
		return true; // Allow forward for dimensions/confirmation steps for now
	};

	const convertOzToLbsAndOz = (weightOz) => {
		const lbs = Math.floor(weightOz / 16);
	  	const oz = weightOz % 16;
	  	return { lbs, oz };
	};

	//set package fields to whichever pre configured box is selected
	const setPackageFields = (length, width, height, weightOz) => {
		const { lbs, oz } = convertOzToLbsAndOz(weightOz);
	  	setPackageLength(length);
	  	setPackageWidth(width);
	  	setPackageHeight(height);
	  	setPackageWeightLbs(lbs);
	  	setPackageWeightOz(oz);
	  	setViewSavedPackageBoxes(false); //close window after selecting a box
	};

    const purchaseLabelManually = async () => {
    	setProgressBarStateIndex(progressBarStateIndex + 1);
    	setShowPurchaseLabelLoader(true);
        try {
            const response = await fetch(`${apiUrl}/api/purchase_shipping_label_manually/`, { 
            	method: "POST", 
            	headers: { 
            		"Content-Type": "application/json",
            		"Authorization": `Bearer ${token}`
            	 },
            	body: JSON.stringify({ "shipment_data": shipmentData, "reference_number": referenceNumber, "save_recipient_address": saveRecipientAddress, "selected_carrier": selectedCarrier, "save_box_flag": saveBox })
            });
            const data = await response.json();
            const label_url = data.label_url;
            setShowPurchaseLabelLoader(false);
            setLabelUrl(label_url);
            window.open(label_url, "_blank");
        } catch (error) { alert("There was an error downloading your label. Please ensure your address is correct."); }
    };

	return (
		<div className={`${styles.label_generator_container} ${fontFamilyOne} fade_in`}>
			<Helmet>
	        	<title>Label Generator</title>
	        </Helmet>
			<LogoWrapper class_={styles.label_generator_logo} src={`${process.env.PUBLIC_URL}/images/partial_logo.png`} />
			<p className={`${styles.label_generator_title} titles`}>{progressBarStateIndex === 4 ? "Label purchased." : progressBarStateIndex === 3 ? "Generate label." : "Create a shipping label."}</p>
			<p className={styles.address_type_text}>{progressBarStateTitle}</p>
			{progressBarStateIndex === 0 && (
				<>
					<div className={`${styles.saved_addresses_search_bar} ${isFocused ? styles.focused : ""}`}>
						<input type="text" placeholder="Search saved addresses" className={`${styles.search_saved_addresses_input} ${fontFamilyOne}`} value={selectedSavedAddress} onChange={handleSearchAddressesChange} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)} />
					</div>
					{showDropdown && (
						<div className={styles.address_search_dropdown}>
							<div className={styles.select_an_address_box}><p className={styles.select_an_address_text}>Select an address.</p></div>
							{savedAddresses.length === 0 ? (<p className={styles.no_saved_addresses_text}>No saved addresses.</p>)
							: (
								savedAddresses.map((addr, idx) => (
									<div key={idx} className={`${styles.address_option_container} ${idx !== savedAddresses.length - 1 ? "bottom_box_shadow" : ""}`} onClick={() => { handleAddressClick(addr); }}>
										<p className={styles.address_option}>{addr}</p>
									</div>
								))
							)}
						</div>
					)}
					{requiredFieldsFilled() && !savedAddresses.includes(`${recipient}, ${recipientStreetOne}, ${recipientStreetTwo ? recipientStreetTwo + ", " : ""}${recipientCity}, ${recipientState}, ${recipientZip}, ${recipientCountry}`) && (
						<div className={`${styles.save_box} ${styles.address} fade_in`}>
							<img src={`${process.env.PUBLIC_URL}/images/${saveRecipientAddress ? "choice_selected" : "choice_unselected"}.png`} alt={saveRecipientAddress ? "choice selected" : "choice unselected"} className={saveRecipientAddress ? styles.save_check_box_selected : styles.save_check_box_unselected} onClick={() => setSaveRecipientAddress(!saveRecipientAddress)} />
							<p className={styles.save_address_text}>Save address</p>
						</div>
					)}
				</>
			)}
			{progressBarStateIndex < 2 && (
				<div className={`${styles.address_input_container} ${progressBarStateIndex === 1 ? styles.move : ''}`}>
					<div className={styles.address_field}>
						<TextInput inputType={progressBarStateIndex === 0 ? "recipient" : "sender"} value={progressBarStateIndex === 0 ? recipient : sender} onChange={(e) => progressBarStateIndex === 0 ? (setRecipient(e.target.value), setSelectedSavedAddress("")) : setSender(e.target.value)} />
						<p className="asterisk">*</p>
					</div>
					<div className={styles.address_field}>
						<TextInput inputType={"street1"} value={progressBarStateIndex === 0 ? recipientStreetOne : senderStreetOne} onChange={(e) => progressBarStateIndex === 0 ? setRecipientStreetOne(e.target.value) : setSenderStreetOne(e.target.value)} />
						<p className="asterisk">*</p>
					</div>
					<div className={styles.address_field}>
						<TextInput inputType={"street2"} value={progressBarStateIndex === 0 ? recipientStreetTwo : senderStreetTwo} onChange={(e) => progressBarStateIndex === 0 ? setRecipientStreetTwo(e.target.value) : setSenderStreetTwo(e.target.value)} />
						<p className="asterisk" style={{ opacity: 0 }}>*</p>
					</div>
					<div className={styles.address_field_container}>
						<div className={styles.address_field}>
							<TextInput inputType={"city"} value={progressBarStateIndex === 0 ? recipientCity : senderCity} onChange={(e) => progressBarStateIndex === 0 ? setRecipientCity(e.target.value) : setSenderCity(e.target.value)} customClass={styles.label_inputs} />
							<p className="asterisk">*</p>
						</div>
						<div className={styles.address_field}>
							<TextInput inputType={"state"} value={progressBarStateIndex === 0 ? recipientState : senderState} onChange={(e) => progressBarStateIndex === 0 ? setRecipientState(e.target.value) : setSenderState(e.target.value)} customClass={styles.label_inputs} />
							<p className="asterisk">*</p>
						</div>
					</div>
					<div className={styles.address_field_container}>
						<div className={styles.address_field}>
							<TextInput inputType={"zip_code"} value={progressBarStateIndex === 0 ? recipientZip : senderZip} onChange={(e) => progressBarStateIndex === 0 ? setRecipientZip(e.target.value) : setSenderZip(e.target.value)} customClass={styles.label_inputs} />
							<p className="asterisk">*</p>
						</div>
						<div className={styles.address_field}>
							<TextInput inputType={"country"} value={progressBarStateIndex === 0 ? recipientCountry : senderCountry} onChange={(e) => progressBarStateIndex === 0 ? setRecipientCountry(e.target.value) : setSenderCountry(e.target.value)} customClass={styles.label_inputs} />
							<p className="asterisk">*</p>
						</div>
					</div>
				</div>
			)}
			{progressBarStateIndex === 2 && (
				<div className={styles.package_dimensions_container}>
					{viewSavedPackageBoxes && (
						<>
							<img src={`${process.env.PUBLIC_URL}/images/remove_popup.png`} alt="remove_view_saved_packages_button" className={styles.remove_view_saved_packages} onClick={() => setViewSavedPackageBoxes(false)} />
							<div className={styles.package_box_selection_container}>
							  	<div className={styles.saved_package_list}>
							  		{savedPackageBoxes.length === 0 ? (
							        	<div className={styles.no_saved_packages}>
							            	No saved packages.
							          	</div>
							        ) : (
							    		savedPackageBoxes.map(box => (
							      			<div key={box.id} className={styles.saved_package_card} onClick={() => { setPackageFields(box.length, box.width, box.height, box.weight); }}>
							        			<div className={styles.saved_package_text_container}>
							        				<p className={styles.saved_package_text}>L. {box.length} in. × W. {box.width} in. × H. {box.height} in. × Wt. {box.weight} oz.</p>
							        			</div>
							        			<img src={`${process.env.PUBLIC_URL}/images/box_icon.png`} alt="box_icon" className={styles.box_icon} />
							      			</div>
							    		))
							    	)}
							  	</div>
							</div>
						</>
					)}
					<div className={styles.view_saved_button} onClick={() => setViewSavedPackageBoxes(true) }>View saved</div>
					<div className={styles.address_field}>
						<p className="asterisk">*</p>
						<TextInput inputType={"length"} value={packageLength} onChange={(e) => setPackageLength(e.target.value)} customClass={styles.label_inputs} />
						<p className="dimension_labels">in.</p>
					</div>
					<div className={styles.address_field}>
						<p className="asterisk">*</p>
						<TextInput inputType={"width"} value={packageWidth} onChange={(e) => setPackageWidth(e.target.value)} customClass={styles.label_inputs} />
						<p className="dimension_labels">in.</p>
					</div>
					<div className={styles.address_field}>
						<p className="asterisk">*</p>
						<TextInput inputType={"height"} value={packageHeight} onChange={(e) => setPackageHeight(e.target.value)} customClass={styles.label_inputs} />
						<p className="dimension_labels">in.</p>
					</div>
					<p className={styles.weight_title}>Weight.</p>
					<div className={styles.weight_container}>
						<div className={styles.address_field}>
							<p className="asterisk" style={{ opacity: 0 }}>*</p>
							<TextInput inputType={"pounds"} value={packageWeightLbs} onChange={(e) => setPackageWeightLbs(e.target.value)} customClass={styles.weight_inputs} />
							<p className="dimension_labels">lbs.</p>
						</div>
						<p className={styles.weight_divider}>X</p>
						<div className={styles.address_field}>
							<p className="asterisk">*</p>
							<TextInput inputType={"ounces"} value={packageWeightOz} onChange={(e) => setPackageWeightOz(e.target.value)} customClass={styles.weight_inputs} />
							<p className="dimension_labels">oz.</p>
						</div>
					</div>
					{requiredFieldsFilled() && !dimensionsAlreadySaved() && (
						<div className={`${styles.save_box} ${styles.box} fade_in`}>
							<img src={`${process.env.PUBLIC_URL}/images/${saveBox ? "choice_selected" : "choice_unselected"}.png`} alt={saveBox ? "choice selected" : "choice unselected"} className={saveBox ? styles.save_check_box_selected : styles.save_check_box_unselected} onClick={() => setSaveBox(!saveBox)} />
							<p className={styles.save_box_text}>Save box</p>
						</div>
					)}
				</div>
			)}
			{progressBarStateIndex === 3 && shipmentData && (
				<>
					<LabelGeneratorConfirmationBox data={shipmentData} selectedCarrier={selectedCarrier}/>
					<div className={styles.reference_number_and_carrier_options_box}>
						<div className={styles.reference_number_box}>
							<TextInput inputType={"reference_number"} value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)} customClass={styles.reference_number_input}/>
						</div>
						<div className={styles.carrier_options_wrapper}>
							<p className={styles.choose_carrier_text}>Carrier:</p>
							<div className={`${styles.carrier_logo} ${styles.cheapest_button} ${selectedCarrier === "cheapest" && styles.carrier_selected}`} onClick={() => setSelectedCarrier("cheapest")}>Cheapest</div>
							<img src={`${process.env.PUBLIC_URL}/images/usps_logo.png`} alt="usps_logo" className={`${styles.carrier_logo} ${selectedCarrier === "usps" && styles.carrier_selected}`} onClick={() => setSelectedCarrier("usps")}/>
							<img src={`${process.env.PUBLIC_URL}/images/ups_logo.png`} alt="ups_logo" className={`${styles.carrier_logo} ${selectedCarrier === "ups" && styles.carrier_selected}`} onClick={() => setSelectedCarrier("ups")}/>
						</div>
					</div>
				</>
			)}
			{progressBarStateIndex === 4 && (
				<>
					{showPurchaseLabelLoader && (
						<>
							<img className={styles.purchase_label_loader} src={`${process.env.PUBLIC_URL}/images/shipwheel_loader.gif`} alt={"purchase_label_loader"}/>
							<p className={styles.downloading_label_text}>Downloading...</p>
						</>
					)}
					{!showPurchaseLabelLoader && (
						<>
							<button className={`login_buttons ${styles.label_url_button} ${fontFamilyOne}`} onClick={() => { window.open(labelUrl, "_blank"); }}>See label</button>
							<button className={`login_buttons ${styles.new_label_button} ${fontFamilyOne}`} onClick={() => { window.location.reload(); }}>Purchase new label</button>
						</>
					)}
				</>
			)}
			{progressBarStateIndex !== 4 && (
				<div className={`${styles.back_forward_button_container} ${progressBarStateIndex > 0 ? styles.move : ""}`} style={{ width: progressBarStateIndex === 3 ? "250px" : undefined }}>
					<BackButton button_id={`${styles.label_generator_back_button} ${progressBarStateIndex === 0 ? styles.hide : ""}`} image_id={styles.label_generator_back_button_arrow} customBack={handleBack} />
					{progressBarStateIndex !== 3 && (<BackButton button_id={`${styles.label_generator_forward_button} ${progressBarStateIndex === 0 ? styles.shift : ""} ${!requiredFieldsFilled() ? styles.disabled : ""}`} image_id={styles.label_generator_forward_button_arrow} customBack={requiredFieldsFilled() ? handleNext : () => {}} />)}
					{progressBarStateIndex === 3 && (<button className={`login_buttons ${styles.purchase_label_button} ${!referenceNumber ? styles.dim : ""} ${fontFamilyOne}`} onClick={() => { purchaseLabelManually(); }} disabled={!referenceNumber}>Purchase label</button>)}
				</div>
			)}
			<LabelGeneratorProgressBar steps={progressBarStates} currentIndex={progressBarStateIndex} />
		</div>
	);
};

export default LabelGenerator;